/**
 * EditPanel — Feature digitization + geometry editing (node, split, merge)
 */
import React, { useCallback } from 'react';
import { Box, Typography, Divider, Alert, Button } from '@mui/material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import DigitizationToolbar from '../../DigitizationToolbar';
import NodeEditingToggle from '../../NodeEditingToggle';
import SplitToolToggle from '../../SplitToolToggle';
import MergeToolButton from '../../MergeToolButton';
import { startDigitization, stopDigitization } from '../../../store/slices/digitizationSlice';
import { setMeasurementMode } from '../../../store/slices/measurementSlice';
import { setActiveTool as setDrawingTool } from '../../../store/slices/drawingSlice';
import {
  deactivateNodeEditing,
  deactivateSplitTool,
} from '../../../store/slices/geometryEditingSlice';
import { getDefaultAttributeFields } from '../../../types/layerCreation';
import type { LayerGeometryType } from '../../../types/layerCreation';

const EditPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);
  const layers = useAppSelector((s) => s.layers.layers);
  const digMode = useAppSelector((s) => s.digitization.mode);
  const activeLayer = layers.find((l) => l.id === activeLayerId);
  const isDigitizing = digMode === 'drawing' || digMode === 'attribute-input';

  const handleStartEdit = useCallback(() => {
    if (!activeLayer || activeLayer.type !== 'vector') return;
    dispatch(setMeasurementMode(null));
    dispatch(setDrawingTool(null));
    dispatch(deactivateSplitTool());
    dispatch(deactivateNodeEditing());
    const geom = (activeLayer.geometryType || 'Polygon') as LayerGeometryType;
    dispatch(
      startDigitization({
        layerId: activeLayer.id,
        geometryType: geom,
        attributeFields: activeLayer.attributeFields?.length
          ? activeLayer.attributeFields
          : getDefaultAttributeFields(geom),
      })
    );
  }, [activeLayer, dispatch]);

  const handleStopEdit = useCallback(() => {
    dispatch(stopDigitization());
  }, [dispatch]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {!activeLayer && (
        <Alert severity="info" sx={{ m: 1.5, fontSize: tokens.fontSize.sm }}>
          Select a vector layer in Layers to start editing.
        </Alert>
      )}

      {activeLayer && activeLayer.type === 'raster' && (
        <Alert severity="warning" sx={{ m: 1.5, fontSize: tokens.fontSize.sm }}>
          Raster layers cannot be digitized. Select a vector layer.
        </Alert>
      )}

      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
            mb: 1,
          }}
        >
          Digitization
        </Typography>
        {activeLayer && activeLayer.type === 'vector' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {!isDigitizing ? (
              <Button
                size="small"
                variant="contained"
                onClick={handleStartEdit}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Start editing “{activeLayer.name}”
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={handleStopEdit}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Stop editing
              </Button>
            )}
          </Box>
        )}
        <DigitizationToolbar />
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
            mb: 1,
          }}
        >
          Geometry editing
        </Typography>
        <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.secondary, mb: 1 }}>
          Select a feature on the map, then use node edit, split, or merge.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <NodeEditingToggle />
          <SplitToolToggle />
          <MergeToolButton />
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 1.5, py: 1.5 }}>
        <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.muted }}>
          Tip: open the attribute table from the header to edit table values. Changes sync to the
          map instantly.
        </Typography>
        <Button
          size="small"
          sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
          onClick={() => window.dispatchEvent(new CustomEvent('geovera:open-attributes'))}
        >
          Open attribute table
        </Button>
      </Box>
    </Box>
  );
};

export default EditPanel;
